import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Building2, Shield, MessageSquare, Search, ArrowRight, ShieldCheck, ChevronDown, ChevronUp, FileText, Scale, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { getLanguageFromCnpjEin } from "../utils/languageUtils";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// FAQ Data
const faqData = {
  pt: [
    {
      question: "Minhas informações são realmente confidenciais?",
      answer: "Sim. Todo o sistema é projetado para garantir sigilo absoluto. Se você optar por fazer uma denúncia anônima, não será possível identificá-lo. Suas informações são criptografadas e protegidas."
    },
    {
      question: "Como acompanho o andamento da minha denúncia?",
      answer: "Ao registrar uma denúncia, você receberá um número de protocolo e uma senha. Com esses dados, poderá acompanhar o status da investigação a qualquer momento através da opção 'Acompanhar Protocolo'."
    },
    {
      question: "Posso fazer uma denúncia mesmo sendo anônimo?",
      answer: "Sim! Você pode escolher fazer uma denúncia totalmente anônima ou se identificar opcionalmente. A escolha é sua. Mesmo anônimo, sua denúncia será investigada com o mesmo rigor."
    },
    {
      question: "Qual o prazo para resposta?",
      answer: "Todas as denúncias são analisadas em até 48 horas úteis. O prazo para conclusão da investigação varia conforme a complexidade do caso, mas você será informado de cada etapa pelo sistema."
    },
    {
      question: "Posso anexar provas à minha denúncia?",
      answer: "Sim. Você pode anexar documentos, fotos, áudios e vídeos que possam ajudar na investigação. Os arquivos aceitos incluem PDF, DOC, imagens (JPG, PNG) e arquivos de mídia."
    }
  ],
  en: [
    {
      question: "Is my information really confidential?",
      answer: "Yes. The entire system is designed to ensure absolute secrecy. If you choose to make an anonymous report, it will not be possible to identify you. Your information is encrypted and protected."
    },
    {
      question: "How do I track my report?",
      answer: "When registering a report, you will receive a protocol number and password. With this data, you can track the status of the investigation at any time through the 'Track Protocol' option."
    },
    {
      question: "Can I make an anonymous report?",
      answer: "Yes! You can choose to make a completely anonymous report or optionally identify yourself. The choice is yours. Even anonymous, your report will be investigated with the same rigor."
    },
    {
      question: "What is the response time?",
      answer: "All reports are analyzed within 48 business hours. The deadline for completing the investigation varies according to the complexity of the case, but you will be informed of each step through the system."
    },
    {
      question: "Can I attach evidence to my report?",
      answer: "Yes. You can attach documents, photos, audios and videos that may help in the investigation. Accepted files include PDF, DOC, images (JPG, PNG) and media files."
    }
  ]
};

export default function EmpresaLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAcompanhar, setShowAcompanhar] = useState(false);
  const [protocolo, setProtocolo] = useState("");
  const [senha, setSenha] = useState("");
  const [acompanhando, setAcompanhando] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  
  // Determinar idioma baseado no CNPJ/EIN da empresa
  // CNPJ (Brasil) = português | EIN (EUA) = inglês
  const currentLang = empresa ? getLanguageFromCnpjEin(empresa.cnpj_ein) : 'pt';

  useEffect(() => {
    fetchEmpresa();
  }, [slug]);

  const fetchEmpresa = async () => {
    try {
      // Add cache-busting to ensure fresh data
      const res = await axios.get(`${API_URL}/empresas/${slug}/check`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      if (res.data.exists) {
        setEmpresa(res.data);
      } else {
        navigate("/");
        toast.error("Empresa não encontrada");
      }
    } catch (error) {
      navigate("/");
      toast.error("Empresa não encontrada");
    } finally {
      setLoading(false);
    }
  };

  const handleAcompanhar = async (e) => {
    e.preventDefault();
    setAcompanhando(true);
    
    try {
      await axios.post(`${API_URL}/empresas/${slug}/denuncias/acompanhar`, {
        protocolo,
        senha,
      });
      navigate(`/${slug}/acompanhar?protocolo=${protocolo}&senha=${senha}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Protocolo ou senha inválidos");
    } finally {
      setAcompanhando(false);
    }
  };

  const content = {
    pt: {
      welcome: {
        title: "Bem-vindo ao Canal de Ética",
        subtitle: "Este é um canal seguro e confidencial para você registrar denúncias, sugestões, reclamações ou elogios. Escolha abaixo como deseja prosseguir."
      },
      whistleblowing: {
        title: "Canal de Denúncias",
        description: "Registre uma denúncia de forma <strong>100% anônima ou identificada</strong>. Você receberá um protocolo e senha para acompanhar o andamento.",
        button: "Fazer Denúncia",
        items: ["Fraude e desvios financeiros", "Assédio moral ou sexual", "Segurança do trabalho", "Corrupção e suborno"]
      },
      ombudsman: {
        title: "Ouvidoria",
        description: "Envie sugestões, reclamações, elogios ou solicite informações. Neste canal, sua identificação é <strong>necessária</strong>.",
        button: "Acessar Ouvidoria",
        items: ["Sugestões de melhoria", "Reclamações", "Elogios", "Solicitação de informações"]
      },
      track: {
        title: "Acompanhar Protocolo",
        subtitle: "Já registrou uma denúncia? Acompanhe aqui.",
        button: "Consultar Protocolo",
        protocol: "Número do Protocolo",
        password: "Senha de Acesso",
        submit: "Acompanhar",
        loading: "Consultando..."
      },
      noRetaliation: {
        title: "Política de Não Retaliação",
        description: "Esta empresa possui uma política rigorosa de não retaliação. Nenhum colaborador será penalizado ou sofrerá represálias por fazer denúncias de boa-fé através deste canal."
      },
      faq: {
        title: "Perguntas Frequentes"
      },
      footer: {
        lgpd: "LGPD",
        privacy: "Política de Privacidade",
        terms: "Termos de Uso",
        ethics: "Código de Ética",
        powered: "Powered by OmbudNow"
      }
    },
    en: {
      welcome: {
        title: "Welcome to the Ethics Channel",
        subtitle: "This is a secure and confidential channel for you to register reports, suggestions, complaints or compliments. Choose below how you want to proceed."
      },
      whistleblowing: {
        title: "Whistleblowing Channel",
        description: "Register a report <strong>100% anonymously or identified</strong>. You will receive a protocol and password to track the progress.",
        button: "Make a Report",
        items: ["Fraud and financial misconduct", "Moral or sexual harassment", "Work safety", "Corruption and bribery"]
      },
      ombudsman: {
        title: "Ombudsman",
        description: "Send suggestions, complaints, compliments or request information. In this channel, your identification is <strong>required</strong>.",
        button: "Access Ombudsman",
        items: ["Improvement suggestions", "Complaints", "Compliments", "Information requests"]
      },
      track: {
        title: "Track Protocol",
        subtitle: "Already registered a report? Track it here.",
        button: "Check Protocol",
        protocol: "Protocol Number",
        password: "Access Password",
        submit: "Track",
        loading: "Checking..."
      },
      noRetaliation: {
        title: "Non-Retaliation Policy",
        description: "This company has a strict non-retaliation policy. No employee will be penalized or suffer retaliation for making good-faith reports through this channel."
      },
      faq: {
        title: "Frequently Asked Questions"
      },
      footer: {
        lgpd: "LGPD",
        privacy: "Privacy Policy",
        terms: "Terms of Use",
        ethics: "Code of Ethics",
        powered: "Powered by OmbudNow"
      }
    }
  };

  const c = content[currentLang];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a2e]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{empresa?.nome ? `${empresa.nome} - Canal de Ética e Ouvidoria` : 'Canal de Ética e Ouvidoria'}</title>
        <meta name="description" content={`Canal de denúncias anônimas e ouvidoria da ${empresa?.nome || 'empresa'}. Reporte irregularidades com total sigilo e segurança. Protegido pela LGPD.`} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={`${window.location.origin}/${slug}`} />
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-5">
            {empresa?.logo_url ? (
              <img
                src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : (empresa.logo_url.startsWith('http') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`)}
                alt={empresa.nome}
                className="h-12 sm:h-20 w-auto"
              />
            ) : (
              <div className="w-12 h-12 sm:w-20 sm:h-20 bg-[#1a1a2e] rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 sm:w-10 sm:h-10 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-[#1a1a2e]">{empresa?.nome}</h1>
              <p className="text-sm sm:text-base text-slate-500">Canal de Ética e Ouvidoria</p>
            </div>
          </div>
          <Link to={`/${slug}/login`}>
            <Button variant="outline" size="sm" data-testid="btn-admin">
              {currentLang === 'en' ? 'Admin Area' : 'Área Administrativa'}
            </Button>
          </Link>
        </div>
      </header>

      {/* Company Banner - If configured - Scales with page zoom */}
      {empresa?.banner_url && (
        <div style={{ width: '100vw', marginLeft: 'calc(-50vw + 50%)', overflow: 'hidden' }}>
          <img
            src={empresa.banner_url.startsWith('data:') ? empresa.banner_url : (empresa.banner_url.startsWith('http') ? empresa.banner_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.banner_url}`)}
            alt={`${empresa.nome} banner`}
            style={{ 
              width: '100vw', 
              height: 'auto', 
              display: 'block'
            }}
          />
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full">
        {/* Welcome Section */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1a2e] mb-4">
            {c.welcome.title}
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-sm sm:text-base">
            {c.welcome.subtitle}
          </p>
        </div>

        {/* Non-Retaliation Banner */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-green-800 mb-1 text-sm sm:text-base">{c.noRetaliation.title}</h3>
            <p className="text-green-700 text-xs sm:text-sm">{c.noRetaliation.description}</p>
          </div>
        </div>

        {/* Options Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {/* Canal de Denúncias */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#1a1a2e]/10 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
              <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-[#1a1a2e]" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-[#1a1a2e] mb-2 sm:mb-3">
              {c.whistleblowing.title}
            </h3>
            <p className="text-slate-600 mb-4 sm:mb-6 text-sm sm:text-base" dangerouslySetInnerHTML={{ __html: c.whistleblowing.description }} />
            <ul className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6 space-y-2">
              {c.whistleblowing.items.map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#1a1a2e] rounded-full flex-shrink-0"></span>
                  {item}
                </li>
              ))}
            </ul>
            <Link to={`/${slug}/denuncia`}>
              <Button className="w-full bg-[#1a1a2e] hover:bg-[#2d2d4a]" data-testid="btn-denuncia">
                {c.whistleblowing.button}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Ouvidoria */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#f5a623]/10 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
              <MessageSquare className="w-6 h-6 sm:w-7 sm:h-7 text-[#f5a623]" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-[#1a1a2e] mb-2 sm:mb-3">
              {c.ombudsman.title}
            </h3>
            <p className="text-slate-600 mb-4 sm:mb-6 text-sm sm:text-base" dangerouslySetInnerHTML={{ __html: c.ombudsman.description }} />
            <ul className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6 space-y-2">
              {c.ombudsman.items.map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#f5a623] rounded-full flex-shrink-0"></span>
                  {item}
                </li>
              ))}
            </ul>
            <Link to={`/${slug}/ouvidoria`}>
              <Button className="w-full bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e]" data-testid="btn-ouvidoria">
                {c.ombudsman.button}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Track Protocol Section */}
        <div className="bg-white rounded-2xl border border-slate-200 p-8 mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center">
              <Search className="w-7 h-7 text-slate-600" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-[#1a1a2e]">{c.track.title}</h3>
              <p className="text-slate-600 text-xs sm:text-sm">{c.track.subtitle}</p>
            </div>
          </div>
          
          {!showAcompanhar ? (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setShowAcompanhar(true)}
              data-testid="btn-show-acompanhar"
            >
              {c.track.button}
            </Button>
          ) : (
            <form onSubmit={handleAcompanhar} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">{c.track.protocol}</Label>
                  <Input
                    value={protocolo}
                    onChange={(e) => setProtocolo(e.target.value)}
                    placeholder="DEN-XXXXXX"
                    required
                    data-testid="input-protocolo"
                  />
                </div>
                <div>
                  <Label className="text-sm">{c.track.password}</Label>
                  <Input
                    type="password"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="input-senha"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#1a1a2e]" 
                disabled={acompanhando}
                data-testid="btn-acompanhar"
              >
                {acompanhando ? c.track.loading : c.track.submit}
              </Button>
            </form>
          )}
        </div>

        {/* FAQ Section */}
        <div className="mb-8 sm:mb-12">
          <h3 className="text-xl sm:text-2xl font-bold text-[#1a1a2e] text-center mb-6 sm:mb-8">{c.faq.title}</h3>
          <div className="space-y-3">
            {faqData[currentLang].map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden"
              >
                <button
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-medium text-[#1a1a2e] text-sm sm:text-base pr-2">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1a1a2e] text-white py-8 mt-auto">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <Link to={currentLang === 'en' ? '/en/privacy-policy' : '/pt/politica-de-privacidade'} className="hover:text-[#f5a623] transition-colors flex items-center gap-2">
                <Lock className="w-4 h-4" />
                {c.footer.lgpd}
              </Link>
              <Link to={currentLang === 'en' ? '/en/privacy-policy' : '/pt/politica-de-privacidade'} className="hover:text-[#f5a623] transition-colors flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {c.footer.privacy}
              </Link>
              <Link to={currentLang === 'en' ? '/en/terms-of-use' : '/pt/termos-de-uso'} className="hover:text-[#f5a623] transition-colors flex items-center gap-2">
                <Scale className="w-4 h-4" />
                {c.footer.terms}
              </Link>
              <Link to={currentLang === 'en' ? '/en/code-of-ethics' : '/pt/codigo-de-etica'} className="hover:text-[#f5a623] transition-colors flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                {c.footer.ethics}
              </Link>
            </div>
            <div className="text-slate-400 text-sm">
              {c.footer.powered}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
